/**
 *  FELUGRÓ ABLAKOK VALÓDI ABLAKBAN (asztali program)
 *  ============================================================================
 *
 *  Az asztali (Electron) programban a modálok (ion-modal) nem a főablakon belül, rétegként nyílnak,
 *  hanem külön, előtérben lévő operációsrendszer-ablakban – áthelyezhetők, átméretezhetők, másik
 *  képernyőre vihetők (user, 2026-10-07).
 *
 *  HOGYAN. A modál megnyílik a megszokott módon, majd a TARTALMA (az Angular-nézet DOM-ja) átköltözik
 *  egy azonos eredetű gyerekablakba (window.open). A JavaScript a főablakban fut tovább: a kötések, a
 *  gombok, a beírt adat ugyanazok – csak a megjelenítés helye más. Így egyetlen modálhoz sem kellett
 *  hozzányúlni.
 *   – a stílusok (a főablak <head>-jének style/link elemei, és ami később érkezik) átmásolódnak;
 *   – a tartalom egy `ion-modal` nevű (a gyerekablakban nem regisztrált, tehát sima) burokba kerül,
 *     a modál osztályaival – így a modálokra írt témaszabályok (fejlécszín, méretek) ott is érvényesek;
 *   – a főablakban a modál nem látszik, és NINCS sötétítés: a főablak közben is teljesen használható
 *     (görgethető, kattintható, gépelhető) – több ablakkal párhuzamosan lehet dolgozni (user, 2026-10-07);
 *   – a gyerekablak bezárása = a modál bezárása, a modál bezárása = az ablak bezárása;
 *   – a modálból nyíló megerősítő/figyelmeztető ablakok (alert, popover, toast, loading) is a
 *     gyerekablakba költöznek, különben a főablakban, a háta mögött jelennének meg.
 *  Ha bármi nem sikerül (pl. a böngésző letiltja az ablakot), a modál marad a régi módon.
 *
 *  BEKAPCSOLÁS: CSAK az asztali programban (window.CRONOM_ASZTALI) – weben a modálok maradnak.
 */

import { modalIllesztesIndit, modalOldal, tartalomFigyel, termeszetesMagassag } from './modal-illesztes';

interface Kiemelt { modal: HTMLElement; ablak: Window; figyelo: MutationObserver; idozito: any; keret: HTMLElement; wrapper: HTMLElement | null; backdropDismiss: any; illesztesVege?: () => void; }
const aktiv: Kiemelt[] = [];
let sorszam = 0;

/*  CSAK AZ ASZTALI PROGRAMBAN (a helyi kiszolgáló injektálja: window.CRONOM_ASZTALI). Weben és
 *  mobilon a modálok maradnak modálok – ezt semmilyen kapcsoló nem írhatja felül (user, 2026-10-07). */
function bekapcsolva(): boolean {
  //  Az asztali program menüjében (Nézet → Felugró ablakok külön ablakban) kikapcsolható.
  const a = (window as any).CRONOM_ASZTALI;
  return !!a && a.ablakModal !== false;
}

const STILUS = `
  html, body { margin: 0; height: 100%; overflow: hidden; background: #fff; }
  ion-modal.ablak-modal-keret { display: block !important; position: absolute !important; inset: 0 !important;
    width: auto !important; height: auto !important; contain: none !important; overflow: hidden; }
  ion-modal.ablak-modal-keret > .ion-page { position: absolute !important; inset: 0 !important; display: flex !important;
    flex-direction: column !important; contain: none !important; }
  /* A tartalomhoz méretezett modálok oldala/törzse a modálban max. 92vh – itt az ablak magassága (a törzs görget,
     a fejléc és a gombsor marad). */
  ion-modal.ablak-modal-keret > .ion-page { max-height: none !important; }
  ion-modal.ablak-modal-keret .nk-torzs, ion-modal.ablak-modal-keret .sa-torzs, ion-modal.ablak-modal-keret .bv-torzs,
  ion-modal.ablak-modal-keret .rlb-body { max-height: none !important; flex: 1 1 auto; min-height: 0; overflow: auto; }
  /* Az ion-content-es (rögzített magasságú) modál a külön ablakban: a fejléc és a lábléc a helyén, a tartalom
     kitölti a maradékot és BELÜL görget. A gyerekablakba költözött Ionic-elemek árnyék-stílusa (a főablak
     adoptedStyleSheets-e) nem jön át, ezért a görgetőt a ::part(scroll)-lal állítjuk – különben a lábléc
     (a Mentés gomb) az ablak alja alá csúszott (mérve, Electron). */
  ion-modal.ablak-modal-keret > .ion-page > :is(ion-header, ion-footer) { flex: 0 0 auto; }
  ion-modal.ablak-modal-keret > .ion-page > ion-content { display: block; position: relative; flex: 1 1 auto !important;
    min-height: 0; height: auto !important; }
  ion-modal.ablak-modal-keret > .ion-page > ion-content::part(scroll) { position: absolute; inset: 0; overflow-y: auto; }
`;

function stilusMasol(d: Document, n: Node) {
  if (n.nodeName === 'STYLE' || (n.nodeName === 'LINK' && (n as HTMLLinkElement).rel === 'stylesheet')) d.head.appendChild(n.cloneNode(true));
}

function kiemel(modal: HTMLElement) {
  if (!bekapcsolva() || modal.dataset['ablakModal']) return;
  const tartalom = Array.from(modal.children).filter((c) => !/^(TEMPLATE|ION-BACKDROP)$/.test(c.nodeName));
  if (!tartalom.length) { mutat(modal); return; }
  const wrapper = modal.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement | null;
  const r = wrapper?.getBoundingClientRect();
  const w = Math.round(Math.min(Math.max(r?.width || 900, 480), (screen.availWidth || 1400) - 40));
  const h = Math.round(Math.min(Math.max((r?.height || 700) + 30, 360), (screen.availHeight || 900) - 60));
  const left = Math.round(window.screenX + Math.max(0, (window.outerWidth - w) / 2));
  const top = Math.round(window.screenY + Math.max(0, (window.outerHeight - h) / 2));
  let ablak: Window | null = null;
  //  Névvel nyitjuk (cronom-ablak-<n>): így a főablakból, hibakereséskor is elérhető.
  try { ablak = window.open('', `cronom-ablak-${++sorszam}`, `popup=yes,width=${w},height=${h},left=${left},top=${top}`); } catch { ablak = null; }
  if (!ablak) { mutat(modal); return; }                  // letiltva – marad a modál (látható)
  modal.dataset['ablakModal'] = '1';
  const d = ablak.document;
  d.open();
  d.write(`<!doctype html><html><head><meta charset="utf-8"><base href="${document.baseURI}"></head><body></body></html>`);
  d.close();
  d.title = (modal.querySelector('ion-title')?.textContent || document.title || '').trim();
  //  A téma a <html>/<body> osztályain és változóin múlik (naplo-aktiv, ber-aktiv, mód, sötét…).
  for (const a of Array.from(document.documentElement.attributes)) d.documentElement.setAttribute(a.name, a.value);
  d.body.className = document.body.className;
  const bs = document.body.getAttribute('style'); if (bs) d.body.setAttribute('style', bs);
  document.head.querySelectorAll('style, link[rel="stylesheet"]').forEach((n) => stilusMasol(d, n));
  const sajat = d.createElement('style'); sajat.textContent = STILUS; d.head.appendChild(sajat);
  //  A később betöltődő komponensstílusok (az Angular a főablak <head>-jébe teszi) is jöjjenek át.
  const figyelo = new MutationObserver((ms) => ms.forEach((m) => m.addedNodes.forEach((n) => stilusMasol(d, n))));
  figyelo.observe(document.head, { childList: true });

  const keret = d.createElement('ion-modal');            // a gyerekablakban nem regisztrált elem: csak burok
  keret.className = `${modal.className} ablak-modal-keret`;
  const ms = modal.getAttribute('style'); if (ms) keret.setAttribute('style', ms);
  //  A főablakbeli rejtés (elrejt) NEM jöhet át: különben a külön ablak tartalma is láthatatlan (fehér ablak).
  keret.style.removeProperty('visibility');
  keret.style.removeProperty('pointer-events');
  d.body.appendChild(keret);
  tartalom.forEach((c) => keret.appendChild(c));          // a DOM átköltözik, a JS marad
  //  A FŐABLAK SZABAD MARAD (többablakos munka). A modál a főablakban láthatatlan és átkattintható
  //  (a sötétítő réteggel együtt), és kikapcsoljuk, amivel az Ionic egy nyitott modálnál a főablakot
  //  lezárja: a fókuszcsapdát (különben a főablak mezőjébe kattintva a fókusz visszaugrana a modálba),
  //  a görgetés-tiltást és a háttér `aria-hidden` jelölését. A főablakban leütött Escape se zárja be.
  modal.style.setProperty('visibility', 'hidden', 'important');
  modal.style.setProperty('pointer-events', 'none', 'important');
  modal.classList.add('ion-disable-focus-trap');
  const eredetiBackdrop = (modal as any).backdropDismiss;
  (modal as any).backdropDismiss = false;
  hatterFelold();

  //  Escape: mint a modálnál – bezárás (a modál saját canDismiss-szabálya dönt).
  d.addEventListener('keydown', (e) => { if (e.key === 'Escape') (modal as any).dismiss?.(undefined, 'backdrop'); });
  const k: Kiemelt = { modal, ablak, figyelo, idozito: null, keret, wrapper, backdropDismiss: eredetiBackdrop };
  //  A gyerekablak bezárása (X) a modált is zárja.
  k.idozito = setInterval(() => {
    if (ablak!.closed) { lezar(k, false); (modal as any).dismiss?.(undefined, 'ablak'); }
  }, 400);
  aktiv.push(k);
  //  Az ablak magassága a tartalomhoz: a Mentés gombig ne kelljen görgetni (user, 2026-11-03).
  const page = modalOldal(keret);
  if (page) k.illesztesVege = ablakIllesztes(ablak, page);
  try { ablak.focus(); } catch { /* */ }
}

/**
 *  A KÜLÖN ABLAK MAGASSÁGA A TARTALOMHOZ. A nyitó méret a főablakbeli modál dobozából jön – az viszont a
 *  modál CSS-korlátja (Ionic-alap 500/600 px, 85vh, a főablak magassága) miatt sokszor kisebb a
 *  tartalomnál, és a Mentés gombig görgetni kellett. Az áthelyezés után megmérjük a fejléc + a teljes
 *  törzs + a lábléc magasságát, és az ablakot akkorára nyújtjuk (a képernyő magasságáig; ha alul
 *  kilógna, feljebb tolva). A szélesség marad. Később betöltődő tartalomnál újramér – amíg a
 *  felhasználó kézzel át nem méretezi az ablakot (onnantól az ő mérete érvényes). Csak NŐ.
 */
function ablakIllesztes(ablak: Window, page: HTMLElement): () => void {
  let sajatIg = Date.now() + 1500;                        // a nyitáskori resize-események még nem kézi méretezések
  let kezi = false;
  const atmeretezve = () => { if (Date.now() > sajatIg) kezi = true; };
  const illeszt = () => {
    if (kezi || ablak.closed) return;
    const kell = termeszetesMagassag(page);
    if (kell <= ablak.innerHeight + 2) return;
    const kep = ablak.screen || screen;
    const availH = kep.availHeight || 900;
    const availTop = (kep as any).availTop ?? 0;
    const cel = Math.min(kell + Math.max(0, ablak.outerHeight - ablak.innerHeight), availH);
    if (cel <= ablak.outerHeight + 1) return;
    sajatIg = Date.now() + 1000;
    try {
      ablak.resizeTo(ablak.outerWidth, cel);
      if (ablak.screenY + cel > availTop + availH) ablak.moveTo(ablak.screenX, Math.max(availTop, availTop + availH - cel));
    } catch { /* */ }
  };
  ablak.addEventListener('resize', atmeretezve);
  //  Az első mérés, amikor a gyerekablak már kirajzolta az áthelyezett tartalmat.
  ablak.requestAnimationFrame(() => ablak.requestAnimationFrame(illeszt));
  const figyelesVege = tartalomFigyel(page, illeszt);
  return () => { figyelesVege(); try { ablak.removeEventListener('resize', atmeretezve); } catch { /* */ } };
}

/** Az Ionic nyitott modálnál a főablak görgetését tiltja és a hátteret elrejti a felolvasó elől – a
 *  kiemelt (külön ablakba költözött) modáloknál ez nem kell. Csak ha nincs a főablakban maradt modál. */
function hatterFelold() {
  const maradt = Array.from(document.querySelectorAll('ion-modal.show-modal, ion-alert, ion-action-sheet, ion-popover, ion-loading'))
    .some((m) => !(m as HTMLElement).dataset['ablakModal'] && !(m as any).classList.contains('overlay-hidden'));
  if (maradt) return;
  document.body.classList.remove('backdrop-no-scroll');
  document.querySelector('ion-router-outlet[aria-hidden], #ion-view-container-root[aria-hidden]')?.removeAttribute('aria-hidden');
}

/** Nyitáskor azonnal láthatatlan és átkattintható a főablakban. */
function elrejt(modal: HTMLElement) {
  if (!bekapcsolva() || !modal || modal.dataset['ablakModal']) return;
  modal.style.setProperty('visibility', 'hidden', 'important');
  modal.style.setProperty('pointer-events', 'none', 'important');
}
/** Ha mégsem lett külön ablak (letiltva, üres), a modál a régi módon látszik. */
function mutat(modal: HTMLElement) {
  modal.style.removeProperty('visibility');
  modal.style.removeProperty('pointer-events');
}

function lezar(k: Kiemelt, ablakotIs = true) {
  clearInterval(k.idozito);
  k.figyelo.disconnect();
  try { k.illesztesVege?.(); } catch { /* */ }
  const i = aktiv.indexOf(k); if (i >= 0) aktiv.splice(i, 1);
  /*  A MODÁL VISSZAÁLLÍTÁSA. Ugyanaz a modál-elem újra megnyílhat (a sablonban álló `<ion-modal>`
   *  minden megnyitásnál ugyanaz): a kiemelés jelzése és a rejtés nélkül újra kiemelhető, különben
   *  a második megnyitás láthatatlanul nyílt ki (user, 2026-10-07). A tartalom visszaköltözik, hogy az
   *  Angular a megszokott helyén bontsa le (vagy a megtartott tartalom a helyén legyen). */
  const m = k.modal;
  try { Array.from(k.keret.childNodes).forEach((n) => m.appendChild(n)); } catch { /* a bezárt ablak tartalma */ }
  delete m.dataset['ablakModal'];
  m.classList.remove('ion-disable-focus-trap');
  (m as any).backdropDismiss = k.backdropDismiss;
  //  A láthatóság csak a bezárás UTÁN jön vissza – különben a záró animáció alatt a modál felvillanna a főablakban.
  const lathato = () => {
    if (m.dataset['ablakModal']) return;                  // közben újra megnyitották és kiemelték
    m.style.removeProperty('visibility');
    m.style.removeProperty('pointer-events');
    if (k.wrapper) k.wrapper.style.visibility = '';
  };
  m.addEventListener('ionModalDidDismiss', lathato, { once: true });
  setTimeout(lathato, 1500);                              // ha a bezárás-esemény elmaradna
  if (ablakotIs && !k.ablak.closed) { try { k.ablak.close(); } catch { /* */ } }
}

/*  A modálból nyíló többi felület (alert, popover, toast, loading, action sheet, picker) a legfelső
 *  kiemelt ablakba költözik, különben a főablakban – a gyerekablak mögött – jelenne meg. */
function overlayAtkoltoztet(e: Event) {
  const el = e.target as HTMLElement;
  const k = aktiv[aktiv.length - 1];
  if (!k || !el || k.ablak.closed || el.ownerDocument === k.ablak.document) return;
  try { k.ablak.document.body.appendChild(el); k.ablak.focus(); } catch { /* marad a főablakban */ }
}

export function ablakModalIndit(): void {
  //  A (főablakbeli, rétegként nyíló) modálok magassága a tartalomhoz – weben és mobilon is (modal-illesztes.ts).
  modalIllesztesIndit();
  if (!bekapcsolva()) return;
  //  A modál már a nyitás KEZDETÉN láthatatlan (a sötétítéssel együtt) – különben a nyitó animáció alatt
  //  egy pillanatra a főablakban is felvillant (user, 2026-10-07). A rejtés a méretet nem rontja el
  //  (visibility), így a külön ablak mérete a modáléból számolható. A tartalom a nyitás végén költözik.
  document.addEventListener('ionModalWillPresent', (e) => elrejt(e.target as HTMLElement), true);
  document.addEventListener('ionModalDidPresent', (e) => kiemel(e.target as HTMLElement), true);
  const zar = (e: Event) => { const k = aktiv.find((x) => x.modal === e.target); if (k) lezar(k); };
  document.addEventListener('ionModalWillDismiss', zar, true);
  document.addEventListener('ionModalDidDismiss', zar, true);
  for (const ev of ['ionAlertWillPresent', 'ionPopoverWillPresent', 'ionToastWillPresent', 'ionLoadingWillPresent',
    'ionActionSheetWillPresent', 'ionPickerWillPresent']) document.addEventListener(ev, overlayAtkoltoztet, true);
  //  A főablak bezárásakor a nyitott gyerekablakok is.
  window.addEventListener('beforeunload', () => aktiv.slice().forEach((k) => lezar(k)));
}
